import { useState } from "react"
import type { MouseEvent, SyntheticEvent } from "react"

import { useTranslation } from "react-i18next"

import type { ResponseContextDataPoint } from "@/api/models"

import { Book, ChevronDown, Eye, Globe } from "@/components/icons"
import { MarkdownRenderer } from "@/components/markdown-renderer"
import { cn } from "@/lib/utils"

import { parseSupportingContentItems } from "./supporting-content-utils"
import { buttonVariants } from "../../../components/ui/button-variants"
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip"
import { useCitationPreview } from "../contexts/use-citation-preview"

// moved above to maintain import group ordering

type SupportingContentProps = {
  supportingContent:
    | string[]
    | { text: string[]; images?: { url: string }[] }
    | ResponseContextDataPoint
  citationSection?: string
}

export function SupportingContent({ supportingContent, citationSection }: SupportingContentProps) {
  const { t } = useTranslation()
  const textItems = Array.isArray(supportingContent) ? supportingContent : supportingContent.text
  const imageItems = !Array.isArray(supportingContent) ? supportingContent.images : []
  const { openPreview } = useCitationPreview()

  const [expandedMap, setExpandedMap] = useState<Record<number, boolean>>({})
  const parsedItems = parseSupportingContentItems(textItems)

  const allExpanded =
    parsedItems.length > 0 && parsedItems.every((_, idx) => expandedMap[idx] !== false)

  const toggleItem = (idx: number) => {
    setExpandedMap((prev: Record<number, boolean>) => ({ ...prev, [idx]: !(prev[idx] ?? true) }))
  }
  const setAllExpanded = (value: boolean) => {
    const next: Record<number, boolean> = {}
    parsedItems.forEach((_, idx) => {
      next[idx] = value
    })
    setExpandedMap(next)
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex justify-end">
        <button
          className={cn(buttonVariants({ size: "sm", variant: "ghost" }))}
          type="button"
          onClick={() => setAllExpanded(!allExpanded)}
        >
          {allExpanded ? t("analysispanel.collapse_all") : t("analysispanel.expand_all")}
        </button>
      </div>
      {parsedItems.map((item, index) => {
        const isExpanded = expandedMap[index] ?? true
        const collapsedLabel = item.title
        let baseUrl = ""
        if (item.link) {
          try {
            baseUrl = new URL(item.link).hostname
          } catch {
            baseUrl = ""
          }
        }
        const iconSrc = baseUrl ? `https://icons.duckduckgo.com/ip3/${baseUrl}.ico` : ""

        const renderIcon = () => {
          if (item.citationId) {
            return <Book className="text-muted-foreground size-3" />
          }
          if (baseUrl) {
            return (
              <>
                <img
                  alt={`${baseUrl} favicon`}
                  className="size-3"
                  src={iconSrc}
                  onError={(e: SyntheticEvent<HTMLImageElement, Event>) => {
                    e.currentTarget.style.display = "none"
                    const sibling = e.currentTarget.nextElementSibling as HTMLElement
                    sibling.classList.remove("hidden")
                  }}
                />
                <Globe className="text-muted-foreground hidden size-3" />
              </>
            )
          }
          return <Globe className="text-muted-foreground size-3" />
        }

        return (
          <Card
            key={index}
            className={cn(!isExpanded && "cursor-pointer")}
            role="presentation"
            onClick={() => {
              if (!isExpanded) {
                toggleItem(index)
              }
            }}
          >
            <CardHeader
              className="relative pr-10"
              role="presentation"
              onClick={() => {
                if (isExpanded) {
                  toggleItem(index)
                }
              }}
            >
              <div className="relative flex items-center gap-2 pr-8">
                <button
                  type="button"
                  aria-label={
                    isExpanded
                      ? t("analysispanel.collapse", { defaultValue: "Collapse" })
                      : t("analysispanel.expand", { defaultValue: "Expand" })
                  }
                  className={cn(
                    buttonVariants({ size: "icon", variant: "ghost" }),
                    "h-6 w-6 p-0 transition-transform",
                    isExpanded ? "rotate-180" : "rotate-0"
                  )}
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation()
                    toggleItem(index)
                  }}
                >
                  <ChevronDown className="size-4" />
                </button>
                <div className="bg-muted flex size-5 shrink-0 items-center justify-center rounded-full">
                  {renderIcon()}
                </div>
                <CardTitle className="break-all">
                  {isExpanded ? item.title : collapsedLabel}
                </CardTitle>

                {item.citationId && citationSection && (
                  <Tooltip>
                    <TooltipTrigger
                      type="button"
                      className={cn(
                        buttonVariants({ size: "icon", variant: "ghost" }),
                        "absolute top-1/2 right-0 -mt-0.5 h-6 w-6 -translate-y-1/2 p-0"
                      )}
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation()
                        openPreview(item.citationId || "", citationSection || "", {
                          chunkText: item.contentHtml || undefined,
                        })
                      }}
                    >
                      <Eye className="size-4" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
                      <p>{t("answer.show_document")}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              {isExpanded && item.link && (
                <a
                  className="text-muted-foreground text-sm break-all underline underline-offset-4"
                  href={item.link}
                  rel="noreferrer"
                  target="_blank"
                >
                  {item.link}
                </a>
              )}
              {isExpanded && item.subtitle && (
                <p className="text-muted-foreground text-sm break-all">
                  {item.citationId ? (
                    <>
                      <strong>{t("pages.common.library")}</strong>: {item.subtitle}
                    </>
                  ) : (
                    item.subtitle
                  )}
                </p>
              )}
            </CardHeader>
            {isExpanded && (
              <CardContent className="bg-muted/20 border-t pt-4">
                {imageItems?.map((image, imgIndex) => {
                  return <img key={imgIndex} alt="" className="object-contain" src={image.url} />
                })}
                <MarkdownRenderer className="text-sm" content={item.contentHtml} />
              </CardContent>
            )}
          </Card>
        )
      })}
    </section>
  )
}
